import Loader from '@/components/atoms/Loader';
import UserProfileOverview from '@/components/organisms/UserProfileOverview';
import { employeeUserRoutes } from '@/constants/routes/app';
import useToast from '@/hooks/useToast';
import UserPeerSupportBioModal from '@/modules/user/UserPeerSupportBioModal';
import { uploadProfilePhoto } from '@/services/api/requests/user';
import { useAppStore } from '@/store/useAppStore';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import UserWellnessTimeline from '@/modules/user/UserWellnessTimeline';
import { useOwnTimeline } from '@/hooks/timeline';
import { useState } from 'react';
import useSyncUserDataToStore from '@/hooks/user/useSyncUserDataToStore';
import roles from '@/constants/roles';

const UserProfile = () => {
  useSyncUserDataToStore(); // re-fetch user data on dashboard check

  const toast = useToast();
  const navigate = useNavigate({ from: employeeUserRoutes.userProfile });
  const { user: userData, setUser } = useAppStore();
  const isContentCreatorOnly = userData?.roles.length === 1 && userData?.roles.includes(roles.contentCreator);

  const { data: timelineItems, isLoading: isLoadingTimelineItems } = useOwnTimeline();

  const [isBioModalOpen, setIsBioModalOpen] = useState(false);

  const onClickEditContactDetails = () => {
    navigate({ to: '/user/profile/contact' });
  };

  const onClickEditDemographics = () => {
    navigate({ to: '/user/profile/demographics' });
  };

  const uploadPhoto = async (profilePhoto: File) => {
    try {
      const data = await uploadProfilePhoto(profilePhoto);
      setUser(data);
      toast({
        status: 'success',
        title: 'Success',
        description: 'Profile Photo updated successfully!',
        position: 'top-right',
      });
      return true;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      return false;
    }
  };

  if (!userData) return <Loader />;

  const demographics = userData.user_metadata;
  const hasDemographics = Boolean(
    demographics.dateOfBirth || demographics.sex || demographics.ethnicity || demographics.relationshipStatus ||
    demographics.education || demographics.occupation || demographics.learningStylePreference?.length,
  );

  return (
    <>
      <UserProfileOverview
        showSkippedDemographics={!hasDemographics}
        title={'My Profile'}
        updateProfilePhoto={uploadPhoto}
        userData={{ ...userData.user_metadata, email: userData.email }}
        onClickEditContactDetails={onClickEditContactDetails}
        onClickEditDemographics={onClickEditDemographics}
        onClickEditPeerSupportBio={() => setIsBioModalOpen(true)}
        initialPreview={userData?.user_metadata.profilePicture}
        userRoles={userData.roles}
      />

      {!isContentCreatorOnly && (
        <UserWellnessTimeline isLoading={isLoadingTimelineItems} timelineItems={timelineItems} />
      )}

      <UserPeerSupportBioModal
        isModalOpen={isBioModalOpen}
        setIsModalOpen={setIsBioModalOpen}
        userMetadata={userData.user_metadata}
      />
    </>
  );
};

export const Route = createFileRoute('/_authenticated/_user/_profile/user/profile')({
  staticData: { title: 'Profile' },
  component: UserProfile,
});
